import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useIdleTimerContext } from 'react-idle-timer';

import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { IDLE_WARNING_COUNTDOWN_SECONDS } from 'src/components/modals/idle-warning/idle-warning';
import { readConciergeConversationId } from '../conversation-storage';
import { trackIdleWarning } from './track-idle-warning';
import { useKioskSessionReset } from './use-kiosk-session-reset';

// Same countdown as the shared kiosk idle warning.
export const CONCIERGE_IDLE_COUNTDOWN_SECONDS = IDLE_WARNING_COUNTDOWN_SECONDS;

// Upper bound on waiting for the click that follows a dismissing tap.
const GESTURE_END_FALLBACK_MS = 500;

// Any of these anywhere in the document means a shopper is present. Capture
// phase so a tap inside a host modal or drawer still reaches us. No `scroll`:
// programmatic scrolls (transcript auto-scroll) are indistinguishable from input.
const CONTINUE_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'wheel'] as const;

type ConciergeIdleWarningProps = {
  onClose: () => void;
  countdownSeconds?: number;
};

/**
 * Kiosk concierge idle warning. Deliberately separate from the shared kiosk
 * warning, which pauses the timer, requires an explicit button press, and
 * offers a destructive "empty cart" exit — behavior that is correct for End
 * Session and phone login but wrong here.
 *
 * This variant leaves the host timer running, treats any interaction as
 * "still here", and has no exit that ends the session; only the countdown
 * reaching zero resets the terminal, and that delegates to the existing kiosk
 * reset path.
 */
export function ConciergeIdleWarning({
  onClose,
  countdownSeconds = CONCIERGE_IDLE_COUNTDOWN_SECONDS,
}: ConciergeIdleWarningProps): JSX.Element {
  const idleTimer = useIdleTimerContext();
  const resetKioskSession = useKioskSessionReset();
  const { dispensary } = useDispensary();
  const conversationId = readConciergeConversationId(dispensary?.id);
  const [secondsLeft, setSecondsLeft] = useState(countdownSeconds);
  // Guards both paths: whichever fires first wins, and neither can fire twice.
  const settledRef = useRef(false);

  const conversationIdRef = useRef(conversationId);
  conversationIdRef.current = conversationId;

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const resetRef = useRef(resetKioskSession);
  resetRef.current = resetKioskSession;

  const keepShoppingRef = useRef<HTMLButtonElement>(null);

  // Move focus into the dialog, and give it back on dismiss if the opener is still on the page.
  useEffect(() => {
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    keepShoppingRef.current?.focus();
    return () => {
      if (previouslyFocused?.isConnected) {
        previouslyFocused.focus();
      }
    };
  }, []);

  useEffect(() => {
    trackIdleWarning('shown', { conversationId: conversationIdRef.current });
  }, []);

  const [isSettled, setIsSettled] = useState(false);
  const backdropRef = useRef<HTMLDivElement>(null);
  const clearGestureRef = useRef<(() => void) | null>(null);

  // Keep the backdrop up until the tap's synthesized click has been swallowed (or a
  // short fallback elapses), so the dismissing tap cannot also activate content beneath it.
  const closeAfterGesture = useCallback(() => {
    const finish = (): void => {
      clearGestureRef.current?.();
      clearGestureRef.current = null;
      onCloseRef.current();
    };
    const swallowClick = (event: Event): void => {
      event.stopPropagation();
      event.preventDefault();
      finish();
    };
    // The backdrop covers the screen until finish(), so the dismissing tap's own click
    // lands on it. Clicks on anything else (a higher-z modal) are left alone.
    const backdrop = backdropRef.current;
    const timer = setTimeout(finish, GESTURE_END_FALLBACK_MS);
    backdrop?.addEventListener('click', swallowClick);
    clearGestureRef.current = () => {
      clearTimeout(timer);
      backdrop?.removeEventListener('click', swallowClick);
    };
  }, []);

  const continueSession = useCallback(
    (isGesture = false) => {
      if (settledRef.current) return;
      settledRef.current = true;
      setIsSettled(true);
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- context is null with no provider
      idleTimer?.reset();
      trackIdleWarning('continued', { conversationId: conversationIdRef.current });
      if (isGesture) {
        closeAfterGesture();
        return;
      }
      onCloseRef.current();
    },
    [idleTimer, closeAfterGesture]
  );

  useEffect(
    () => () => {
      clearGestureRef.current?.();
    },
    []
  );

  // Listeners come off as soon as the warning settles, either way.
  useEffect(() => {
    if (isSettled) return undefined;
    const handler = (event: Event): void =>
      continueSession(event.type === 'pointerdown' || event.type === 'touchstart');
    CONTINUE_EVENTS.forEach((eventName) =>
      document.addEventListener(eventName, handler, { capture: true, passive: true })
    );
    return () =>
      CONTINUE_EVENTS.forEach((eventName) => document.removeEventListener(eventName, handler, { capture: true }));
  }, [continueSession, isSettled]);

  useEffect(() => {
    // Derive from a deadline so a throttled or stalled tab still expires on time.
    const deadline = Date.now() + countdownSeconds * 1000;
    const interval = setInterval(() => setSecondsLeft(Math.max(Math.ceil((deadline - Date.now()) / 1000), 0)), 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the deadline is fixed when the warning opens
  }, []);

  useEffect(() => {
    if (secondsLeft > 0 || settledRef.current) return;
    settledRef.current = true;
    setIsSettled(true);
    trackIdleWarning('expired', { conversationId: conversationIdRef.current });
    resetRef.current();
  }, [secondsLeft]);

  return (
    <Backdrop
      ref={backdropRef}
      data-testid='concierge-idle-backdrop'
      role='alertdialog'
      aria-modal='true'
      aria-labelledby='concierge-idle-heading'
      aria-describedby='concierge-idle-body'
    >
      <Panel>
        <Heading id='concierge-idle-heading'>Still shopping?</Heading>
        <Countdown data-testid='concierge-idle-countdown' aria-hidden='true'>
          {secondsLeft}
        </Countdown>
        <Body id='concierge-idle-body'>Touch anywhere to keep your session.</Body>
        <KeepShoppingButton
          ref={keepShoppingRef}
          type='button'
          data-testid='concierge-idle-keep-shopping'
          onClick={() => continueSession()}
        >
          Keep shopping
        </KeepShoppingButton>
      </Panel>
    </Backdrop>
  );
}

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1300;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--dcm-modal-backdrop-color, rgba(0, 0, 0, 0.5));
`;

const Panel = styled.div`
  background: var(--dcm-modal-bg, #ffffff);
  color: var(--dcm-modal-text-color, #485055);
  border-radius: var(--dcm-modal-border-radius, 28px);
  box-shadow: var(--dcm-modal-shadow, 0 5px 15px 0 rgba(0, 0, 0, 0.14));
  padding: 40px;
  max-width: 560px;
  text-align: center;
`;

const Heading = styled.h2`
  margin: 0 0 8px;
  font-size: 28px;
`;

const Countdown = styled.div`
  font-size: 56px;
  font-weight: 700;
  line-height: 1.1;
  color: var(--dcm-modal-text-color, #485055);
`;

const Body = styled.p`
  margin: 8px 0 32px;
  font-size: 20px;
  color: var(--dcm-modal-secondary-text-color, #5d666d);
`;

type ChromeTheme = {
  theme?: { customized?: { colors?: { buttonsLinksFill?: string; buttonsLinksForeground?: string } } };
};
const keepShoppingFill = ({ theme }: ChromeTheme): string =>
  theme?.customized?.colors?.buttonsLinksFill ?? 'var(--dcm-color-primary, #d4a843)';
const keepShoppingInk = ({ theme }: ChromeTheme): string =>
  theme?.customized?.colors?.buttonsLinksForeground ?? 'var(--dcm-color-text-inverted, #ffffff)';

const KeepShoppingButton = styled.button`
  min-height: 56px;
  width: 100%;
  padding: 0 32px;
  font-size: 20px;
  font-weight: 700;
  border: none;
  border-radius: var(--dcm-radius-buttons, 6px);
  /* Fill and ink come as a pair from the kiosk Concierge chrome theme (the same pair as
     the Kiosk Pro filled buttons), so the label stays readable on every store theme. The
     scoped variables keep the button themable by an SDK theme. */
  background: var(--dcm-concierge-idle-keep-shopping-bg, ${keepShoppingFill});
  color: var(--dcm-concierge-idle-keep-shopping-text-color, ${keepShoppingInk});
  cursor: pointer;
`;
